/**
 * MSAL Initialization and Redirect Handler - Pre-React
 * 
 * This module initializes MSAL ONCE before React renders and handles any redirects.
 * This prevents "interaction_in_progress" errors by ensuring MSAL is fully initialized
 * before any React components try to use it.
 */

import { getMsalService, resetMsalService } from '../services/microsoftAuthService';

// Global flag to prevent ANY double initialization across ALL code paths
declare global {
  interface Window {
    __MSAL_INITIALIZING__?: boolean;
    __MSAL_INITIALIZED__?: boolean;
  }
}

/**
 * Clear all MSAL cached state from storage
 * This prevents "interaction_in_progress" errors from stale state
 */
const clearMsalCache = (): void => {
  console.log('[MSAL] Clearing cached state...');
  
  try {
    // Clear sessionStorage MSAL keys
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (key.includes('msal') || key.includes('login.windows') || key.includes('login.microsoftonline')) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear localStorage MSAL keys
    const localKeys = Object.keys(localStorage);
    localKeys.forEach(key => {
      if (key.includes('msal') || key.includes('login.windows') || key.includes('login.microsoftonline')) {
        // Don't clear our app tokens, only MSAL state
        if (key !== 'accessToken' && key !== 'refreshToken') {
          localStorage.removeItem(key);
        }
      }
    });

    console.log('[MSAL] Cache cleared');
  } catch (error) {
    console.warn('[MSAL] Error clearing cache:', error);
  }
};

/**
 * Check if current URL is a Microsoft redirect
 */
export const isMicrosoftRedirect = (): boolean => {
  const hash = window.location.hash;
  const search = window.location.search;
  
  return (
    hash.includes('id_token=') || 
    hash.includes('access_token=') || 
    hash.includes('code=') ||
    search.includes('code=') ||
    search.includes('error=')
  );
};

/**
 * Initialize MSAL once and handle any Microsoft redirect BEFORE React app loads
 * 
 * This function:
 * 1. Always initializes MSAL (once)
 * 2. If there's a redirect, handles it and stores tokens
 * 3. Returns after completion so React can render
 * 
 * Uses global window flag to prevent ANY double initialization
 */
export const initializeMsalAndHandleRedirect = async (): Promise<void> => {
  // CRITICAL: Check global flag to prevent double initialization
  if (window.__MSAL_INITIALIZING__ || window.__MSAL_INITIALIZED__) {
    console.log('[MSAL] Pre-React: MSAL already initializing or initialized, skipping');
    
    // If we're still initializing, wait a bit and check if it's done
    if (window.__MSAL_INITIALIZING__) {
      console.log('[MSAL] Pre-React: Waiting for existing initialization to complete...');
      // Wait up to 5 seconds for initialization
      for (let i = 0; i < 50; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (window.__MSAL_INITIALIZED__) {
          console.log('[MSAL] Pre-React: Existing initialization completed');
          return;
        }
      }
      console.warn('[MSAL] Pre-React: Timeout waiting for initialization');
    }
    return;
  }

  // Set global flag IMMEDIATELY
  window.__MSAL_INITIALIZING__ = true;

  console.log('[MSAL] Pre-React: Starting MSAL initialization...');

  try {
    // Step 1: Check if this is a redirect FIRST (before clearing cache!)
    const isRedirect = isMicrosoftRedirect();
    console.log('[MSAL] Pre-React: Checking for redirect...', {
      isRedirect,
      hash: window.location.hash,
      search: window.location.search,
      href: window.location.href
    });

    // Step 2: Only clear cache if NOT a redirect (preserve MSAL state for redirect handling)
    if (!isRedirect) {
      console.log('[MSAL] Pre-React: No redirect, clearing stale MSAL cache...');
      clearMsalCache();
    } else {
      console.log('[MSAL] Pre-React: Redirect detected, preserving MSAL cache for handling...');
    }

    // Step 3: Initialize MSAL (don't reset if it's a redirect)
    const msalService = getMsalService();
    await msalService.initialize();
    
    // Mark as initialized
    window.__MSAL_INITIALIZED__ = true;
    window.__MSAL_INITIALIZING__ = false;
    
    console.log('[MSAL] Pre-React: MSAL initialized successfully');

    // Step 4: Process redirect if present
    if (!isRedirect) {
      console.log('[MSAL] Pre-React: No redirect detected, MSAL ready for use');
      return;
    }

    console.log('[MSAL] Pre-React: ✅ Microsoft redirect detected, processing...');

    // Step 3: Check if already processed to prevent duplicates
    const processed = sessionStorage.getItem('msal_redirect_processed');
    if (processed) {
      const timeSinceProcessed = Date.now() - parseInt(processed);
      if (timeSinceProcessed < 10000) { // 10 seconds
        console.log('[MSAL] Pre-React: Redirect already processed recently, skipping');
        return;
      }
    }

    // Step 4: Mark as processing
    sessionStorage.setItem('msal_redirect_processed', Date.now().toString());
    sessionStorage.removeItem('msal_login_initiated');

    // Step 5: Handle the redirect (MSAL is already initialized)
    console.log('[MSAL] Pre-React: Calling MSAL handleRedirect()...');
    const response = await msalService.handleRedirect();
    console.log('[MSAL] Pre-React: handleRedirect() returned:', {
      hasResponse: !!response,
      hasIdToken: response?.idToken ? 'YES' : 'NO',
      hasAccount: response?.account ? 'YES' : 'NO'
    });

    if (response && response.idToken) {
      console.log('[MSAL] Pre-React: ✅ Got ID token from redirect, sending to backend...');
      console.log('[MSAL] Pre-React: ID token length:', response.idToken.length);

      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
      console.log('[MSAL] Pre-React: Sending token to backend...', { baseUrl });

      // Send token to backend for validation and session creation
      const loginResponse = await fetch(`${baseUrl}/auth/microsoft-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: response.idToken }),
      });

      console.log('[MSAL] Pre-React: Backend response status:', loginResponse.status);

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json();
        console.error('[MSAL] Pre-React: Backend error:', errorData);
        throw new Error(errorData.detail || 'Microsoft login failed');
      }

      const data = await loginResponse.json();
      console.log('[MSAL] Pre-React: Backend response data:', {
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token
      });

      // Store tokens in localStorage (React AuthContext will pick them up)
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      
      console.log('[MSAL] Pre-React: ✅ Backend tokens stored successfully in localStorage');

      // Clean up URL hash/params
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);

      // Now that we've successfully handled the redirect, clear MSAL cache
      // This prevents issues on next login
      setTimeout(() => {
        sessionStorage.removeItem('msal_redirect_processed');
        console.log('[MSAL] Pre-React: Clearing MSAL cache after successful redirect...');
        clearMsalCache();
      }, 5000);

    } else {
      console.warn('[MSAL] Pre-React: ⚠️ No ID token in response!', { response });
      // Clear cache on error to allow retry
      clearMsalCache();
    }
    
    if (window.location.search.includes('error=')) {
      // Handle error from Microsoft
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      console.error('[MSAL] Pre-React: Microsoft authentication error:', errorDescription || error);
      
      // Store error to display after React loads
      sessionStorage.setItem('msal_auth_error', errorDescription || error || 'Authentication failed');
      
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname);
    }

  } catch (error: any) {
    console.error('[MSAL] Pre-React: Error during initialization/redirect handling:', error);
    
    // Clear the initializing flag on error
    window.__MSAL_INITIALIZING__ = false;
    window.__MSAL_INITIALIZED__ = false;
    
    // Clear MSAL cache to recover from error state
    clearMsalCache();
    
    // Store error to display after React loads (but make it more user-friendly)
    const errorMessage = error.message || 'Authentication failed';
    if (errorMessage.includes('interaction_in_progress')) {
      sessionStorage.setItem('msal_auth_error', 'Please try again. If the issue persists, clear your browser cache.');
    } else {
      sessionStorage.setItem('msal_auth_error', errorMessage);
    }
    
    // Clean up URL if there was a redirect
    if (isMicrosoftRedirect()) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Don't throw - allow React to render even if MSAL fails
  }
};
